import stringToDom from "../HtmlEditor/Tools/string-to-dom.js";

export default class SearchReplace {
    static State = class {
        static #defaultActiveSegment = {
            matchIndex: 0,
            nextSegmentIndex: 0,
            currentSegmentIndex: 0,
            matchCount: 0,
            openedSegments: []
        };

        #replaceRanges;
        #activeSegment;

        constructor(replaceRanges = [], activeSegment = null) {
            const normalizedRanges = Array.isArray(replaceRanges) ? [...replaceRanges] : [];
            const mergedActiveSegment = {
                ...SearchReplace.State.#defaultActiveSegment,
                ...(activeSegment || {})
            };

            mergedActiveSegment.openedSegments = Array.isArray(mergedActiveSegment.openedSegments)
                ? [...mergedActiveSegment.openedSegments]
                : [];

            this.#replaceRanges = Object.freeze(normalizedRanges);
            this.#activeSegment = Object.freeze(mergedActiveSegment);
            Object.freeze(this);
        }

        get replaceRanges() {
            return this.#replaceRanges;
        }

        get activeSegment() {
            return this.#activeSegment;
        }

        withReplaceRanges(value) {
            return new SearchReplace.State(value, this.#activeSegment);
        }

        withAddedReplaceRange(range) {
            return new SearchReplace.State([...this.#replaceRanges, range], this.#activeSegment);
        }

        withActiveSegment(value) {
            return new SearchReplace.State(this.#replaceRanges, value);
        }

        withActiveSegmentPatch(value) {
            return new SearchReplace.State(this.#replaceRanges, {
                ...this.#activeSegment,
                ...(value || {})
            });
        }

        withReset() {
            return new SearchReplace.State([], SearchReplace.State.#defaultActiveSegment);
        }
    };

    /**
     * Entry point for DATA_CHANGED modifier.
     * Mirrors TermTagger._cleanTermOnTypingInside — removes only the search-replace
     * marker wrapper that surrounds the position being typed/deleted into.
     *
     * @param {string} rawData
     * @param {Array}  actions
     * @param {number} position
     * @param {*}      tagsConversion
     * @param {State} state
     * @returns {[string, number]}
     */
    cleanSearchReplaceMarkerOnTypingInside(rawData, actions, position, tagsConversion, state) {
        let currentState = state instanceof SearchReplace.State ? state : new SearchReplace.State();

        if (!actions || !actions.length) {
            return [rawData, position, currentState];
        }

        const doc = stringToDom(rawData);

        for (const action of actions) {
            if (!action.type) {
                continue;
            }

            currentState = this.#processSearchReplaceMarkerNodes(doc, action, tagsConversion, currentState);
        }

        return [doc.innerHTML, position, currentState];
    }

    /**
     * Position-aware DOM traversal that unwraps the one marker node at the edit point.
     *
     * @param {DocumentFragment} doc
     * @param {Object} action
     * @param {*} tagsConversion
     * @param {State} state
     * @returns {State}
     */
    #processSearchReplaceMarkerNodes(doc, action, tagsConversion, state) {
        const _this = this;
        const position = action.position;
        let pointer = 0;
        let currentState = state;

        function traverseNodes(node) {
            if (node.nodeType === Node.TEXT_NODE) {
                const isWithinNode = pointer <= position && pointer + node.length >= position;

                if (!isWithinNode) {
                    pointer += node.length;

                    return false;
                }

                const isInserting = action.type === RichTextEditor.EditorWrapper.ACTION_TYPE.INSERT;
                const isDeletingMarkerTag = !isInserting && _this.#isSearchReplaceMarkerAffected(action);

                if (
                    _this.#isSearchReplaceMarkerNode(node.parentNode) &&
                    (isInserting || isDeletingMarkerTag)
                ) {
                    currentState = _this.#unwrapSearchReplaceMarkerNode(node.parentNode, currentState);

                    return true;
                }

                const siblingToUnwrap = _this.#resolveSearchReplaceSiblingToUnwrap(node);

                if (siblingToUnwrap && !isInserting && isDeletingMarkerTag) {
                    currentState = _this.#unwrapSearchReplaceMarkerNode(siblingToUnwrap, currentState);

                    return true;
                }

                return true;
            }

            // Keep logical position counter aligned with editor's position model.
            if (tagsConversion.isTag(node)) {
                pointer++;

                return false;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                for (const child of node.childNodes) {
                    const changed = traverseNodes(child);

                    // Stop once the affected marker has been handled.
                    if (changed) {
                        return true;
                    }
                }
            }

            return false;
        }

        traverseNodes(doc);

        return currentState;
    }

    /**
     * Returns true when the given node is a search-replace marker span.
     *
     * @param {Node} node
     * @returns {boolean}
     */
    #isSearchReplaceMarkerNode(node) {
        return !!(
            node &&
            node.nodeType === Node.ELEMENT_NODE &&
            node.classList &&
            Array.from(node.classList).some((className) => className.startsWith('search-replace_marker-'))
        );
    }

    /**
     * Replaces the marker node with its own children (unwrap).
     *
     * @param {Element} node
     * @param {State} state
     * @returns {State}
     */
    #unwrapSearchReplaceMarkerNode(node, state) {
        let currentState = state instanceof SearchReplace.State ? state : new SearchReplace.State();
        const indexAttr = node.getAttribute('data-index');
        const index = indexAttr !== null ? parseInt(indexAttr) : null;
        const parent = node.parentNode;

        // Find the root of this DocumentFragment/Element to re-index other markers
        let root = parent;
        while (root.parentNode) {
            root = root.parentNode;
        }

        const fragment = document.createRange().createContextualFragment(node.innerHTML);
        parent.insertBefore(fragment, node);
        parent.removeChild(node);

        if (index === null) {
            return currentState;
        }

        let nextReplaceRanges = [...currentState.replaceRanges];

        if (index >= 0 && index < nextReplaceRanges.length) {
            nextReplaceRanges.splice(index, 1);
        }

        let nextMatchIndex = currentState.activeSegment.matchIndex;
        const nextMatchCount = nextReplaceRanges.length;

        if (index < nextMatchIndex) {
            nextMatchIndex--;
        }

        if (nextMatchIndex >= nextMatchCount && nextMatchIndex > 0) {
            nextMatchIndex--;
        }

        currentState = currentState
            .withReplaceRanges(nextReplaceRanges)
            .withActiveSegmentPatch({
                matchCount: nextMatchCount,
                matchIndex: nextMatchIndex
            });

        const markers = root.querySelectorAll('[class*="search-replace_marker-"]');
        for (let i = 0; i < markers.length; i++) {
            const marker = markers[i];
            const markerIndexAttr = marker.getAttribute('data-index');
            if (markerIndexAttr !== null) {
                let markerIndex = parseInt(markerIndexAttr);
                if (markerIndex > index) {
                    markerIndex--;
                    marker.setAttribute('data-index', markerIndex);
                }

                marker.classList.remove('search-replace_marker-red', 'search-replace_marker-yellow');
                if (markerIndex === currentState.activeSegment.matchIndex) {
                    marker.classList.add('search-replace_marker-red');
                } else {
                    marker.classList.add('search-replace_marker-yellow');
                }
            }
        }

        return currentState;
    }

    /**
     * Handles boundary cases where the caret sits in a text node that is adjacent
     * to a marker node rather than inside it — mirrors TermTagger._resolveSiblingToUnwrap.
     *
     * @param {Text} node
     * @returns {Element|null}
     */
    #resolveSearchReplaceSiblingToUnwrap(node) {
        // Deletion at the left boundary of a marker: cursor lands in the preceding text node.
        if (this.#isSearchReplaceMarkerNode(node.nextSibling)) {
            return node.nextSibling;
        }

        // Marker is the first child of the next sibling wrapper.
        if (
            node.nextSibling &&
            node.nextSibling.childNodes &&
            this.#isSearchReplaceMarkerNode(node.nextSibling.firstChild)
        ) {
            return node.nextSibling.firstChild;
        }

        // Parent-boundary variants: cursor is in the last child of a parent whose
        // next sibling (or its first child) is the marker.
        if (
            node.parentNode &&
            node.parentNode.lastChild === node &&
            node.parentNode.nextSibling
        ) {
            if (this.#isSearchReplaceMarkerNode(node.parentNode.nextSibling)) {
                return node.parentNode.nextSibling;
            }

            if (
                node.parentNode.nextSibling.firstChild &&
                this.#isSearchReplaceMarkerNode(node.parentNode.nextSibling.firstChild)
            ) {
                return node.parentNode.nextSibling.firstChild;
            }
        }

        return null;
    }

    /**
     * Returns true when the action's deleted content itself contains a marker node —
     * meaning the user is actively deleting into a marker boundary.
     *
     * @param {Object} action
     * @returns {boolean}
     */
    #isSearchReplaceMarkerAffected(action) {
        for (const content of action.content ?? []) {
            const dom = content.toDom?.();

            if (!dom) {
                continue;
            }

            if (
                this.#isSearchReplaceMarkerNode(dom) ||
                (
                    dom.nodeType === Node.ELEMENT_NODE &&
                    dom.querySelector('[class*="search-replace_marker-"]')
                )
            ) {
                return true;
            }
        }

        return false;
    }
}
